import { useEffect, useState } from 'react';
import { Entry } from 'contentful';
import InfiniteScroll from 'react-infinite-scroll-component';

import { client } from '../../helpers/contentful';
import { Spinner, SeoIntro } from '../../components';
import { CourseCard } from '../../components/CourseCard';
import { ICourseFields } from '../../@types/generated/contentful';
import { analytics } from '../../helpers/analytics';
import { ArrowRight } from '../../Icons';

const learnBannerVideo =
  'https://firebasestorage.googleapis.com/v0/b/tools-4372b.appspot.com/o/learn-promo.mp4?alt=media&token=b8a762e9-156e-4518-ba83-3b13bf1d85be';

const Academy = () => {
  const [courses, setCourses] = useState<Entry<ICourseFields>[]>([]);
  const [total, setTotal] = useState(0);

  const getCourses = async () => {
    const response = await client
      .getEntries<ICourseFields>({
        skip: courses.length,
        limit: 12,
        content_type: 'course',
      })
      .then((r) => r);

    if (total === 0) {
      setTotal(response.total);
    }

    setCourses((state) => [...state, ...response.items]);
    return response.items;
  };

  useEffect(() => {
    getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLearnBannerClick = () => {
    analytics.track("Learn Banner Clicked", {
      source: "academy_banner",
      courses_loaded: courses.length,
      total_count: total,
    });

    document.getElementById("academy-courses")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className='home'>
      <SeoIntro
        eyebrow='Academy'
        title='Procreate lessons, tutorials, and artist workflows'
        description='Learn Procreate with practical lessons for brush use, digital illustration, effects, composition, and repeatable iPad art workflows.'
      />
      <div className='border-t-2 w-full'>
        <div className='mx-auto max-w-[1240px] w-full px-6 py-8 md:px-3 md:py-5'>
          <button
            type='button'
            className='group relative block h-[360px] w-full overflow-hidden rounded-[12px] bg-black text-left text-white shadow-[0_18px_48px_rgba(0,0,0,0.12)] md:h-[280px] xs:h-[230px]'
            onClick={handleLearnBannerClick}
            aria-label='View Academy lessons'
          >
            <video
              className='absolute inset-0 block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]'
              autoPlay
              muted
              loop
              playsInline
              preload='metadata'
            >
              <source src={learnBannerVideo} type='video/mp4' />
            </video>
            <span
              className='absolute inset-0'
              style={{
                background:
                  'linear-gradient(90deg, rgba(0, 0, 0, 0.76) 0%, rgba(0, 0, 0, 0.46) 45%, rgba(0, 0, 0, 0.08) 100%)',
              }}
            />
            <span className='relative z-[1] flex h-full max-w-[560px] flex-col justify-end p-8 md:max-w-[440px] md:p-6 xs:p-5'>
              <span className='mb-3 inline-flex w-fit items-center rounded-full bg-white/20 px-3 py-1 text-xxs leading-xxs font-bold backdrop-blur-sm'>
                Learn
              </span>
              <span className='text-4xl leading-4xl font-bold md:text-3xl md:leading-3xl xs:text-2xl xs:leading-2xl'>
                Build your Procreate workflow
              </span>
              <span className='mt-3 text-base leading-base text-white/90 md:text-sm md:leading-sm xs:hidden'>
                Follow practical Academy lessons for brushes, effects, composition, and digital illustration.
              </span>
              <span className='mt-5 inline-flex w-fit items-center gap-2 rounded-[12px] bg-white px-5 py-3 text-sm leading-sm font-bold text-main-blue md:mt-4 md:px-4 md:py-2'>
                Start learning
                <ArrowRight width={20} height={20} />
              </span>
            </span>
          </button>
        </div>
      </div>
      {courses.length > 0 ? (
        <div className='border-t-2 w-full' id='academy-courses'>
          <div className='mx-auto max-w-[1240px] w-full p-6 md:p-3'>
              <InfiniteScroll
                dataLength={courses.length}
                next={getCourses}
                // eslint-disable-next-line react/jsx-boolean-value
                hasMore={courses.length < total}
                scrollableTarget='app'
                style={{ overflow: 'visible' }}
                loader={
                  <div className='mt-4 flex justify-center'>
                    <Spinner theme='light' />
                  </div>
                }
              >
                <ul className='grid grid-cols-[repeat(auto-fill,minmax(300px,350px))] gap-5 md:gap-3 sm:grid-cols-1'>
                  {courses.map(({ fields, sys }) => (
                    <li key={sys.id}>
                      <CourseCard course={fields} />
                    </li>
                  ))}
                </ul>
              </InfiniteScroll>
          </div>
        </div>
      ) : (
        <div className='border-t-2 w-full'>
          <div className='mt-4 flex justify-center p-6'>
            <Spinner theme='light' />
          </div>
        </div>
      )}
    </div>
  );
};

export { Academy };
